// App boot: language, storage persistence, the service worker, the sync engine,
// and mounting the UI. This module is only ever imported when the gate allowed
// the app to run (installed, or desktop).

import { mount } from 'svelte';

import { SETTING_LANGUAGE, SETTING_USER_PHONE } from '$lib/db/setting-keys';
import { adoptStoredLanguage, applyDefaultLanguage } from '$lib/i18n/index.svelte';
import { attachLifecycle, startForegroundPolling, syncAll } from '$lib/protocol/events-loop';

import App from './App.svelte';
import { navigate } from './router.svelte';
import { attachViewport } from './viewport';

async function requestPersistence(): Promise<void> {
  // Ask on EVERY launch, from the very first one. This used to wait until a
  // contact existed, on the reasoning that an empty app has nothing worth
  // protecting — but what needs protecting is not only the rows, it is the
  // service worker's cache and registration, and those exist from the first
  // launch. iOS evicts script-writable storage for apps left unopened, and an
  // evicted worker means the next offline launch shows Safari's own error page
  // instead of the app. Waiting for a contact meant a fresh install never asked
  // at all. Granted is sticky, so `persisted()` keeps this to one call ever.
  // A refusal is survivable by design — the recovery for lost data is SMS
  // re-verification per company.
  try {
    if (!(await navigator.storage.persisted())) {
      const granted = await navigator.storage.persist();
      console.info(`[storage] persistence ${granted ? 'granted' : 'not granted'}`);
    }
  } catch {
    // Older engines without the Storage API — nothing to do.
  }
}

function registerServiceWorker(): void {
  if (import.meta.env.DEV || !('serviceWorker' in navigator)) {
    return;
  }
  // vite-plugin-pwa bundles src/sw.ts to sw.js. A new version installs and then
  // WAITS — it deliberately does not call skipWaiting, because taking over
  // mid-launch means re-downloading the whole precache while someone is staring
  // at the splash. UpdateToast offers the swap once the app is up and running.
  navigator.serviceWorker.register('/sw.js').catch((error: unknown) => {
    console.info('[sw] registration failed', error);
  });
  // A tapped notification in an already-open app: the worker focuses this
  // window and says which thread to show.
  navigator.serviceWorker.addEventListener('message', (event: MessageEvent) => {
    const data = event.data as { type?: unknown; accountId?: unknown } | null;
    if (data !== null && data.type === 'open-thread' && typeof data.accountId === 'string') {
      navigate({ name: 'conversation', accountId: data.accountId });
    }
  });
}

/// Work that must happen, but not while the first screen is still being painted.
/// syncAll reaches the directory and every distributor at once; on a cold radio
/// that is the heaviest thing the app ever does, and doing it in the same tick as
/// the first render makes the user wait for network they cannot see.
function whenIdle(run: () => void): void {
  const idle = (globalThis as { requestIdleCallback?: (cb: () => void) => number })
    .requestIdleCallback;
  if (typeof idle === 'function') {
    idle(run);
  } else {
    // Safari below 17 has no requestIdleCallback; a macrotask after paint is the
    // honest approximation and costs nothing where the real thing exists.
    setTimeout(run, 0);
  }
}

/**
 * @param settings the language and phone rows, already being read by main.ts
 *   since before this chunk finished downloading.
 *
 * The ordering here is the whole point. Nothing awaits a network call, and the
 * one thing it does await — those two settings — was started in the entry chunk,
 * so by the time this runs it has usually already resolved. The splash comes down
 * only after `mount()` has put real content on screen, never before: removing it
 * earlier reveals an empty frame painted in `--bg`, which in dark mode reads as a
 * black screen for however long IndexedDB happens to take.
 */
export async function boot(settings: Promise<Map<string, unknown>>): Promise<void> {
  // Speak the browser's language immediately; swap only if a stored choice says
  // otherwise. This never blocks a paint.
  applyDefaultLanguage();
  attachViewport();
  registerServiceWorker();

  const stored = await settings;
  performance.mark('app:settings');
  adoptStoredLanguage(stored.get(SETTING_LANGUAGE));
  const phone = stored.get(SETTING_USER_PHONE);
  const initialPhone = typeof phone === 'string' ? phone : null;

  const target = document.getElementById('app');
  if (target === null) {
    throw new Error('missing #app element');
  }
  mount(App, { target, props: { initialPhone } });
  performance.mark('app:mounted');

  // There is content underneath now — fade the boot splash away.
  const splash = document.getElementById('splash');
  if (splash !== null) {
    splash.classList.add('gone');
    setTimeout(() => splash.remove(), 250);
  }

  attachLifecycle();
  whenIdle(() => {
    // One call, not two: the second used to exist only because the request was
    // gated on a contact existing, so it had to be retried once sync had found
    // one. Nothing gates it now.
    void requestPersistence();
    void syncAll();
    startForegroundPolling();
  });
}
